/**
 * Non-blocking fixed hint that local simulation progress was saved.
 * Uses pointer-events-none so it never intercepts clicks.
 *
 * @module SimulationDraftSaveIndicator
 */

import { CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useSimulationDraftPersistStore } from '@/stores/useSimulationDraftPersistStore';

export function SimulationDraftSaveIndicator() {
  const phase = useSimulationDraftPersistStore((s) => s.phase);
  const { t } = useTranslation();

  if (phase === 'idle') {
    return null;
  }

  const message =
    phase === 'saving'
      ? t('simulation.draft.saving', { defaultValue: 'Saving…' })
      : t('simulation.draft.savedLocal', {
          defaultValue: 'Draft saved locally',
        });

  return (
    <div
      className={cn(
        'pointer-events-none fixed bottom-4 right-4 z-[100] max-w-[min(90vw,280px)]',
        'rounded-lg border border-border bg-card/95 px-3 py-2 shadow-md backdrop-blur-sm',
        'transition-opacity duration-200',
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="font-ui text-xs font-medium text-muted-foreground">
        {phase === 'saving' ? (
          <Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1.5 align-text-bottom text-primary" aria-hidden />
        ) : (
          <CheckCircle2 className="inline h-3.5 w-3.5 mr-1.5 align-text-bottom text-success" aria-hidden />
        )}
        {message}
      </span>
    </div>
  );
}
